import { css } from '@emotion/react';
import cn from 'classnames';
import { isFunction } from 'lodash';
import { type FocusEventHandler, Fragment, type MemoExoticComponent, memo } from 'react';

import { useFocus } from '@didaskill/ui/utils';

import { EmptyState, type EmptyStateProps } from '../../data-display/empty-state/EmptyState';
import * as styles from './FileUpload.styles';
import { FileUploadContainer, type FileUploadContainerProps } from './FileUploadContainer';
import { FileUploadRules } from './rules/FileUploadRules';

export type FileUploadEmptyStateProps = Omit<FileUploadContainerProps, 'children'> &
  Pick<EmptyStateProps, 'icon' | 'size' | 'title' | 'children'> & {
    /** Root element class name. */
    className?: string;
    /** The dropzone has an error. */
    hasError?: boolean;
    /** The dropzone has a warning. */
    hasWarning?: boolean;
    /** On focus event handler. */
    onFocus?: FocusEventHandler<HTMLDivElement>;
    /** On blur event handler. */
    onBlur?: FocusEventHandler<HTMLDivElement>;
  };

export const FileUploadEmptyStateBase = ({
  title,
  icon = null,
  size = EmptyState.SIZES.MEDIUM,
  hasError = false,
  hasWarning = false,
  children = null,
  className = null,
  onFocus: onFocusProps = undefined,
  onBlur: onBlurProps = undefined,
  ...props
}: FileUploadEmptyStateProps) => {
  const { isFocused, onFocus, onBlur } = useFocus({
    onFocus: onFocusProps,
    onBlur: onBlurProps,
  });

  const rules = (
    <FileUploadRules accept={props.accept} maxSize={props.maxSize} maxFiles={props.maxFiles}>
      {({ extensionsRule, maxSizeRule, maxFilesRule }) =>
        !!(extensionsRule || maxSizeRule || maxFilesRule) && (
          <EmptyState.Paragraph>
            {!!extensionsRule && (
              <Fragment>
                {extensionsRule}
                <br />
              </Fragment>
            )}
            {!!maxSizeRule && (
              <Fragment>
                {maxSizeRule}
                <br />
              </Fragment>
            )}
            {maxFilesRule}
          </EmptyState.Paragraph>
        )
      }
    </FileUploadRules>
  );

  return (
    <FileUploadContainer {...props}>
      {({ getRootProps, getInputProps, isDragAccept, isDragReject, isDropzoneDisabled }) => (
        <div
          {...getRootProps({ onFocus, onBlur })}
          className={cn(className, {
            isFocused,
            isDropzoneDisabled,
            isDragActive: !isDropzoneDisabled, // To make the border look active by default.
            isDragAccept,
            isDragReject,
            hasError,
            hasWarning: !hasError && !!hasWarning,
          })}
          css={styles.dropzone}
        >
          <input {...getInputProps()} />

          <div
            css={css`
              margin: auto;
            `}
          >
            <EmptyState icon={icon} title={title} size={size}>
              {isFunction(children) ? (
                (...args) => (
                  <Fragment>
                    {children(...args)}
                    {rules}
                  </Fragment>
                )
              ) : (
                <Fragment>
                  {children}
                  {rules}
                </Fragment>
              )}
            </EmptyState>
          </div>
        </div>
      )}
    </FileUploadContainer>
  );
};

FileUploadEmptyStateBase.displayName = 'FileUploadEmptyState';

export const FileUploadEmptyState: MemoExoticComponent<typeof FileUploadEmptyStateBase> & {
  SIZES?: typeof EmptyState.SIZES;
  Paragraph?: typeof EmptyState.Paragraph;
} = memo(FileUploadEmptyStateBase);

FileUploadEmptyState.SIZES = EmptyState.SIZES;
FileUploadEmptyState.Paragraph = EmptyState.Paragraph;
